import React from "react";
import { motion } from "framer-motion";
import { Star, Trophy, Gem, Coins, Flame, RotateCw, Ticket, CalendarHeart } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";
import { Section, SectionHead, RarityDot } from "@/components/landing/Section";
import { RARITY_COLORS, XP_TABLE } from "@/lib/trolls";

const EXTRA_ICONS = [Flame, RotateCw, Ticket, CalendarHeart];

const Card = ({ icon: Icon, title, body, children, i, testId }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ delay: i * 0.08 }}
    data-testid={testId}
    className="journal-card p-7 flex flex-col"
  >
    <span className="w-11 h-11 rounded-[12px] bg-amber/15 text-amber grid place-items-center"><Icon className="w-5 h-5" /></span>
    <h3 className="font-display text-xl font-semibold text-ink mt-5">{title}</h3>
    <p className="text-sm text-ink-muted mt-2 leading-relaxed">{body}</p>
    <div className="mt-5 flex-1">{children}</div>
  </motion.div>
);

export default function XPSystem() {
  const { t, fmtNum } = useLang();
  const c = t.xp.cards;
  return (
    <Section id="xp" testId="xp-section" className="bg-paper/60 border-y border-line">
      <SectionHead kicker={t.xp.kicker} title={t.xp.title} body={t.xp.body} />
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-14">
        <Card icon={Star} title={c.xp.title} body={c.xp.body} i={0} testId="xp-card-rarity">
          <ul className="space-y-2.5">
            {XP_TABLE.map(([r, xp]) => (
              <li key={r} className="flex items-center justify-between text-sm font-bold">
                <span className="inline-flex items-center gap-2"><RarityDot color={RARITY_COLORS[r]} /> {t.rarity[r]}</span>
                <span className="tabular-nums text-ink">{fmtNum(xp)} XP</span>
              </li>
            ))}
          </ul>
        </Card>
        <Card icon={Trophy} title={c.ach.title} body={c.ach.body} i={1} testId="xp-card-achievements">
          <ul className="space-y-1.5 text-sm">
            {t.xp.achievements.map(([name, xp]) => (
              <li key={name} className="flex justify-between gap-2"><span className="text-ink">{name}</span><span className="tabular-nums font-bold text-amber">{fmtNum(xp)}</span></li>
            ))}
          </ul>
          <p className="text-xs font-bold text-ink-muted mt-3">{c.ach.more}</p>
        </Card>
        <Card icon={Gem} title={c.items.title} body={c.items.body} i={2} testId="xp-card-items">
          <div className="flex flex-wrap gap-2">
            {c.items.list.map((it) => (
              <span key={it} className="rounded-full border border-dashed border-amber/60 px-3 py-1 text-xs font-bold text-amber">{it}</span>
            ))}
          </div>
        </Card>
        <Card icon={Coins} title={c.kroner.title} body={c.kroner.body} i={3} testId="xp-card-kroner">
          <div className="stamp !text-[11px] rotate-[-4deg] inline-block">TK</div>
        </Card>
      </div>
      <div className="grid md:grid-cols-2 gap-x-12 gap-y-8 mt-16">
        {t.xp.extras.map((e, i) => {
          const Icon = EXTRA_ICONS[i];
          return (
            <div key={e.title} className="flex gap-4" data-testid={`xp-extra-${i}`}>
              <Icon className="w-5 h-5 text-rowan mt-1 shrink-0" />
              <div>
                <h4 className="font-display text-lg font-semibold text-ink">{e.title}</h4>
                <p className="text-ink-muted leading-relaxed mt-1">{e.body}</p>
              </div>
            </div>
          );
        })}
      </div>
    </Section>
  );
}
